import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import applyImageFeatherToSelectionOperation from 'editor/src/store/design/operation/applyImageFeatherToSelectionOperation';
import applyImageMaskToSelectionOperation from 'editor/src/store/design/operation/applyImageMaskToSelectionOperation';
import { ImageMask, ImageMaskShape, MediaImage } from 'editor/src/store/design/types';
import isImageFeatherAllowed from 'editor/src/store/editor/selector/isImageFeatherAllowed';
import isImageMaskAllowed from 'editor/src/store/editor/selector/isImageMaskAllowed';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import useMediaElementLiveUpdates from 'editor/src/util/useMediaElementLiveUpdates';

import { getLocalMaskPoints } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Image/mask/getMaskPolygon';
import SliderWithInput from 'editor/src/component/SliderWithInput';
import WithTooltip from 'editor/src/component/WithTooltip';

import styles from './index.module.scss';

export interface Props {
  selectedElement: MediaImage;
}

type ShapeOption = ImageMaskShape | 'none';

const PREVIEW_WIDTH = 22;
const PREVIEW_HEIGHT = 28;
const PREVIEW_RADIUS = 7;

// drawn from the same outline as the canvas mask, so the tile always shows what the image will get
function ShapePreview({ shape }: { shape: ShapeOption }) {
  const points =
    shape === 'none'
      ? getLocalMaskPoints({ shape: 'rounded-rect', radius: 0 }, PREVIEW_WIDTH, PREVIEW_HEIGHT, 0)
      : getLocalMaskPoints({ shape }, PREVIEW_WIDTH, PREVIEW_HEIGHT, PREVIEW_RADIUS);
  return (
    <svg
      className={styles.preview}
      viewBox={`-1 -1 ${PREVIEW_WIDTH + 2} ${PREVIEW_HEIGHT + 2}`}
      width={PREVIEW_WIDTH + 2}
      height={PREVIEW_HEIGHT + 2}
      aria-hidden
    >
      <polygon points={points.map(([x, y]) => `${x},${y}`).join(' ')} />
    </svg>
  );
}

export const DEFAULT_MASK_RADIUS = 5; // mm

export function getMaxMaskRadius(element: Pick<MediaImage, 'width' | 'height'>) {
  return Math.max(0, Math.floor(Math.min(element.width, element.height) / 2));
}

function ImageMaskControl({ selectedElement }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { liveUpdate } = useMediaElementLiveUpdates(selectedElement);
  const { mask } = selectedElement;
  const maxRadius = getMaxMaskRadius(selectedElement);
  const showShapes = useSelector(isImageMaskAllowed);
  const showFeather = useSelector(isImageFeatherAllowed);
  const feather = selectedElement.feather ?? 0;

  const onFeatherChange = useCallback(
    (value: number) => liveUpdate({ ...selectedElement, feather: value }),
    [selectedElement],
  );

  // compared per image in the operation: the first selected image is not representative of a multi-selection
  const onAfterFeatherChange = useCallback(
    (value: number) => dispatch(applyImageFeatherToSelectionOperation(value)),
    [],
  );

  const shapeOptions: { label: string; value: ShapeOption }[] = useMemo(
    () => [
      { label: t('editor-filter-none'), value: 'none' },
      { label: t('editor-image-shape-oval'), value: 'ellipse' },
      { label: t('editor-image-shape-rounded'), value: 'rounded-rect' },
      { label: t('editor-image-shape-circle'), value: 'circle' },
      { label: t('editor-image-shape-heart'), value: 'heart' },
      { label: t('editor-image-shape-arch'), value: 'arch' },
    ],
    [t],
  );

  const onShapeChange = useCallback(
    (shape: ShapeOption) => {
      // compared per image in the operation: a multi-selection can mix shapes, the first image is not representative
      let newMask: ImageMask | undefined;
      if (shape === 'ellipse' || shape === 'circle' || shape === 'heart' || shape === 'arch') {
        newMask = { shape };
      } else if (shape === 'rounded-rect') {
        newMask = { shape, radius: Math.min(mask?.radius ?? DEFAULT_MASK_RADIUS, maxRadius) };
      }
      dispatch(applyImageMaskToSelectionOperation(newMask));
    },
    [mask, maxRadius],
  );

  const onRadiusChange = useCallback(
    (radius: number) => liveUpdate({ ...selectedElement, mask: { shape: 'rounded-rect', radius } }),
    [selectedElement],
  );

  const onAfterRadiusChange = useCallback(
    // only the rounded rectangles of the selection get the new radius, ovals / unmasked images keep their shape;
    // images that already have this radius are skipped by the operation
    (radius: number) =>
      dispatch(applyImageMaskToSelectionOperation({ shape: 'rounded-rect', radius }, false, 'rounded-rect')),
    [],
  );

  return (
    <div className={cn(styles.ImageMaskControl, 'px-3 py-2 cy-image-mask-control')}>
      {showShapes && (
        <div className={styles.tiles} role="radiogroup">
          {shapeOptions.map(({ label, value }) => {
            const isActive = (mask?.shape ?? 'none') === value;
            return (
              <WithTooltip key={value} overlay={label} placement="top">
                <button
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  aria-label={label}
                  className={cn(
                    styles.tile,
                    { [styles.active]: isActive, [styles.none]: value === 'none' },
                    `cy-image-mask-shape-${value}`,
                  )}
                  onClick={() => onShapeChange(value)}
                >
                  <ShapePreview shape={value} />
                </button>
              </WithTooltip>
            );
          })}
        </div>
      )}
      {showShapes && mask?.shape === 'rounded-rect' && (
        <div className="mt-3">
          <SliderWithInput
            inputClassName="cy-image-mask-radius-input"
            Label={t('editor-image-corner-radius-mm')}
            onChange={onRadiusChange}
            onAfterChange={onAfterRadiusChange}
            min={0}
            max={maxRadius}
            step={1}
            startPoint={0}
            value={Math.min(mask.radius ?? 0, maxRadius)}
            position="twoRows"
          />
        </div>
      )}
      {showFeather && (
        <div className={showShapes ? 'mt-3' : undefined}>
          <SliderWithInput
            inputClassName="cy-image-feather-input"
            Label={t('editor-image-soft-edge-mm')}
            onChange={onFeatherChange}
            onAfterChange={onAfterFeatherChange}
            min={0}
            max={maxRadius}
            step={1}
            startPoint={0}
            value={Math.min(feather, maxRadius)}
            position="twoRows"
          />
        </div>
      )}
    </div>
  );
}

export default React.memo(ImageMaskControl);
